import React, { useState, useEffect } from 'react';
import api from '../api/client';

interface Props {
  auction: any;
  previousRound: any | null;
  onOpened: () => void;
  onCancel: () => void;
}

export default function OpenRoundForm({ auction, previousRound, onOpened, onCancel }: Props) {
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [eligibilities, setEligibilities] = useState<Record<string, number>>({});
  const [bidders, setBidders] = useState<any[]>([]);
  const [durationMinutes, setDurationMinutes] = useState<number | ''>(() => {
    if (previousRound?.startTime && previousRound?.endTime) {
      const mins = (new Date(previousRound.endTime).getTime() - new Date(previousRound.startTime).getTime()) / 60000;
      return Math.round(mins);
    }
    return '';
  });
  const [error, setError] = useState('');

  useEffect(() => {
    // Build eligibility defaults: use calculated values from previous round, fall back to 0
    const prevEligMap: Record<string, number> = {};
    if (previousRound?.eligibilities?.length) {
      for (const e of previousRound.eligibilities) {
        prevEligMap[e.userId] = e.eligibility;
      }
    }
    loadBidders(prevEligMap);

    // Pre-fill prices from previous round, applying increment to categories with excess demand
    if (previousRound) {
      const priceMap: Record<string, number> = {};
      for (const p of previousRound.categoryPrices) {
        const cat = auction.categories.find((c: any) => c.id === p.categoryId);
        const hasExcess = (p.aggregateDemand ?? 0) > (cat?.supply ?? 0);
        const factor = hasExcess ? (1 + (auction.priceIncrement ?? 0) / 100) : 1;
        // Round off floating-point noise (23100 * 1.1 === 25410.000000000004 in IEEE 754)
        // before ceiling, or an already-exact result gets bumped up an extra unit.
        const raw = Math.round(p.price * factor * 1e6) / 1e6;
        priceMap[p.categoryId] = Math.ceil(raw);
      }
      setPrices(priceMap);
    } else {
      const priceMap: Record<string, number> = {};
      for (const cat of auction.categories) {
        priceMap[cat.id] = cat.startingPrice ?? 0;
      }
      setPrices(priceMap);
    }
  }, [auction.categories, previousRound]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadBidders(prevEligMap: Record<string, number> = {}) {
    try {
      const { data } = await api.get('/users/bidders');
      setBidders(data);
      const eligMap: Record<string, number> = {};
      for (const b of data) {
        eligMap[b.id] = prevEligMap[b.id] ?? 0;
      }
      setEligibilities(eligMap);
    } catch {
      setBidders([]);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const pricesPayload = auction.categories.map((cat: any) => ({
        categoryId: cat.id,
        price: prices[cat.id] ?? 0,
      }));
      const eligibilitiesPayload = bidders.map(b => ({
        userId: b.id,
        eligibility: eligibilities[b.id] ?? 0,
      }));
      await api.post(`/auctions/${auction.id}/rounds`, {
        prices: pricesPayload,
        eligibilities: eligibilitiesPayload,
        durationMinutes: durationMinutes !== '' ? durationMinutes : undefined,
      });
      onOpened();
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to open round');
    }
  }

  return (
    <div>
      <h4 style={{ margin: '0 0 1rem' }}>Open Round {(previousRound?.roundNumber ?? 0) + 1}</h4>
      <form onSubmit={handleSubmit} style={styles.form}>
        <h5>Prices per Category</h5>
        {auction.categories.map((cat: any) => (
          <div key={cat.id} style={styles.row}>
            <label style={styles.label}>{cat.name} (supply: {cat.supply})</label>
            <input data-testid={`price-${cat.name}`} style={styles.numInput} type="number" min="0" step="1"
              value={prices[cat.id] ?? ''}
              onChange={e => setPrices(p => ({ ...p, [cat.id]: parseFloat(e.target.value) }))}
              required />
          </div>
        ))}

        <h5>Bidder Eligibilities</h5>
        {bidders.length === 0 && <p style={styles.meta}>No bidders registered yet.</p>}
        {bidders.map(b => (
          <div key={b.id} style={styles.row}>
            <label style={styles.label}>{b.name} ({b.email})</label>
            <input data-testid={`eligibility-${b.name}`} style={styles.numInput} type="number" min="0"
              value={eligibilities[b.id] ?? ''}
              onChange={e => setEligibilities(el => ({ ...el, [b.id]: parseInt(e.target.value) }))}
              required />
          </div>
        ))}

        <div style={styles.row}>
          <label style={styles.label}>Round duration (minutes, optional)</label>
          <input style={styles.numInput} type="number" min="1"
            value={durationMinutes}
            onChange={e => setDurationMinutes(e.target.value ? parseInt(e.target.value) : '')} />
        </div>

        {error && <p style={styles.error}>{error}</p>}
        <div style={styles.actions}>
          <button type="button" style={styles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button type="submit" style={styles.submitBtn}>Open Round</button>
        </div>
      </form>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { border: '1px solid #e0e0e0', borderRadius: '6px', padding: '1rem', marginTop: '1rem', background: '#fafafa' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' },
  label: { fontSize: '0.875rem', color: '#444', flex: 1 },
  numInput: { width: '100px', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' },
  meta: { color: '#888', fontSize: '0.85rem' },
  actions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  cancelBtn: { padding: '0.5rem 1rem', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' },
  submitBtn: { padding: '0.5rem 1rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  error: { color: 'red', fontSize: '0.875rem' },
};
